import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { alpha, createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { themeAPI } from '../services/api';
import { detectTenantId, getActiveTenantProfile } from '../utils/tenantBranding';
import { ensureTenantBrandingDefaults } from '../utils/brandingStorage';
import { isAuthSessionActive } from '../utils/authSession';
import { UI_TOKENS } from '../constants/uiTokens';

// Define color palettes
const colorPalettes = {
  microsoft: {
    primary: '#0078d4',
    secondary: '#106ebe',
    light: '#c7e0f4',
    dark: '#004578',
    contrastText: '#FFFFFF'
  },
  linkedin: {
    primary: '#0a66c2',
    secondary: '#0073b1',
    light: '#c0d9e8',
    dark: '#004182',
    contrastText: '#FFFFFF'
  },
  turquoise: {
    primary: '#20B2AA',
    secondary: '#40E0D0',
    light: '#AFEEEE',
    dark: '#008B8B',
    contrastText: '#FFFFFF'
  },
  blue: {
    primary: '#1976d2',
    secondary: '#42a5f5',
    light: '#bbdefb',
    dark: '#0d47a1',
    contrastText: '#FFFFFF'
  },
  purple: {
    primary: '#9c27b0',
    secondary: '#ba68c8',
    light: '#e1bee7',
    dark: '#6a1b9a',
    contrastText: '#FFFFFF'
  },
  green: {
    primary: '#4caf50',
    secondary: '#81c784',
    light: '#c8e6c9',
    dark: '#2e7d32',
    contrastText: '#FFFFFF'
  },
  orange: {
    primary: '#ff9800',
    secondary: '#ffb74d',
    light: '#ffe0b2',
    dark: '#f57c00',
    contrastText: '#FFFFFF'
  }
};

export type ColorTheme = keyof typeof colorPalettes | 'custom';
export type AppearanceMode = 'light' | 'dark' | 'system';

const APPEARANCE_STORAGE_KEY = 'turboiam-appearance';

interface ThemeContextType {
  currentTheme: ColorTheme;
  setTheme: (theme: ColorTheme) => void;
  availableThemes: ColorTheme[];
  colors: typeof colorPalettes.turquoise;
  setCustomColors: (colors: typeof colorPalettes.turquoise) => void;
  refreshTheme: () => void;
  /** User appearance preference: light, dark, or follow OS. */
  appearance: AppearanceMode;
  setAppearance: (appearance: AppearanceMode) => void;
  /** Resolved MUI palette mode after applying System preference. */
  resolvedMode: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStoredAppearance(): AppearanceMode {
  try {
    const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* ignore */
  }
  return 'system';
}

function applyTenantThemeDefaults(): { theme: ColorTheme; custom: typeof colorPalettes.turquoise | null } {
  const profile = getActiveTenantProfile();
  const hostId = detectTenantId();
  if (hostId === 'mercury' || hostId !== 'provysn' && hostId !== 'default') {
    return { theme: 'custom' as ColorTheme, custom: profile.themeColors };
  }
  if (profile.themeColors.primary !== colorPalettes.microsoft.primary) {
    return { theme: 'custom' as ColorTheme, custom: profile.themeColors };
  }
  return { theme: 'microsoft', custom: null };
}

function parseStoredCustomColors(raw: string | null): typeof colorPalettes.turquoise | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && parsed.primary ? parsed : null;
  } catch {
    return null;
  }
}

function buildMuiTheme(
  colors: typeof colorPalettes.turquoise,
  mode: 'light' | 'dark'
): Theme {
  const isDark = mode === 'dark';
  const defaultTheme = createTheme();
  const primaryContrast = isDark
    ? defaultTheme.palette.getContrastText(colors.primary)
    : colors.contrastText;
  const secondaryContrast = isDark
    ? defaultTheme.palette.getContrastText(colors.secondary)
    : colors.contrastText;
  const darkStatus = {
    success: { surface: alpha('#66bb6a', 0.18), text: '#81c784', border: alpha('#81c784', 0.45) },
    warning: { surface: alpha('#ffa726', 0.18), text: '#ffb74d', border: alpha('#ffb74d', 0.45) },
    error: { surface: alpha('#ef5350', 0.18), text: '#ef9a9a', border: alpha('#ef9a9a', 0.45) },
    info: { surface: alpha('#42a5f5', 0.18), text: '#90caf9', border: alpha('#90caf9', 0.45) },
  };

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: colors.light,
        dark: colors.dark,
        contrastText: primaryContrast,
      },
      secondary: {
        main: colors.secondary,
        light: colors.light,
        dark: colors.dark,
        contrastText: secondaryContrast,
      },
      background: {
        default: isDark ? '#0f1419' : '#f5f5f5',
        paper: isDark ? '#1a2332' : '#ffffff',
      },
      // Remap light greys so existing grey.50 / grey.100 / grey.200 sx usages stay readable in dark mode
      ...(isDark
        ? {
            grey: {
              50: '#151c28',
              100: '#1a2332',
              200: '#243044',
              300: '#2d3748',
              400: '#4a5568',
              500: '#718096',
              600: '#a0aec0',
              700: '#cbd5e0',
              800: '#e2e8f0',
              900: '#f7fafc',
              A100: '#1a2332',
              A200: '#243044',
              A400: '#4a5568',
              A700: '#a0aec0',
            },
          }
        : {}),
      text: {
        primary: isDark ? '#e8eaed' : 'rgba(0, 0, 0, 0.87)',
        secondary: isDark ? '#9aa0a6' : 'rgba(0, 0, 0, 0.6)',
        disabled: isDark ? '#5f6368' : 'rgba(0, 0, 0, 0.38)',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      action: {
        active: isDark ? '#e8eaed' : 'rgba(0, 0, 0, 0.54)',
        hover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        selected: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        disabled: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
        disabledBackground: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      },
    },
    typography: {
      fontFamily: '"Inter", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 },
      h5: { fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 },
      h6: { fontSize: UI_TOKENS.text.bodySm, fontWeight: 600, lineHeight: 1.35 },
      subtitle1: { fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 },
      subtitle2: { fontSize: UI_TOKENS.text.buttonSm, fontWeight: 600 },
      body1: { fontSize: UI_TOKENS.text.bodySm },
      body2: { fontSize: UI_TOKENS.text.buttonSm },
      caption: { fontSize: UI_TOKENS.text.helper },
      button: { fontSize: UI_TOKENS.text.bodySm, textTransform: 'none' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#0f1419' : '#f8fafc',
            color: isDark ? '#e8eaed' : 'inherit',
            scrollbarColor: isDark ? '#5f6368 #1a2332' : '#c1c1c1 #f1f1f1',
            '&::-webkit-scrollbar-track': {
              background: isDark ? '#1a2332' : '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? '#5f6368' : '#c1c1c1',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: isDark ? '#80868b' : '#a8a8a8',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontSize: UI_TOKENS.text.bodySm,
            fontWeight: 600,
            transition: 'background-color 140ms ease, border-color 140ms ease, color 140ms ease',
            ...(isDark && {
              '&.MuiButton-textPrimary': {
                color: colors.light,
                '&:hover': { backgroundColor: alpha(colors.primary, 0.14) },
              },
              '&.MuiButton-textSecondary': {
                color: colors.light,
                '&:hover': { backgroundColor: alpha(colors.secondary, 0.14) },
              },
              '&.MuiButton-outlinedPrimary': {
                color: colors.light,
                borderColor: alpha(colors.light, 0.5),
                backgroundColor: alpha(colors.primary, 0.04),
                '&:hover': {
                  borderColor: colors.light,
                  backgroundColor: alpha(colors.primary, 0.14),
                },
              },
              '&.MuiButton-outlinedSecondary': {
                color: colors.light,
                borderColor: alpha(colors.light, 0.45),
                backgroundColor: alpha(colors.secondary, 0.04),
                '&:hover': {
                  borderColor: colors.light,
                  backgroundColor: alpha(colors.secondary, 0.14),
                },
              },
              '&.MuiButton-outlinedSuccess': {
                color: darkStatus.success.text,
                borderColor: darkStatus.success.border,
                '&:hover': { backgroundColor: darkStatus.success.surface, borderColor: darkStatus.success.text },
              },
              '&.MuiButton-outlinedWarning': {
                color: darkStatus.warning.text,
                borderColor: darkStatus.warning.border,
                '&:hover': { backgroundColor: darkStatus.warning.surface, borderColor: darkStatus.warning.text },
              },
              '&.MuiButton-outlinedError': {
                color: darkStatus.error.text,
                borderColor: darkStatus.error.border,
                '&:hover': { backgroundColor: darkStatus.error.surface, borderColor: darkStatus.error.text },
              },
              '&.MuiButton-outlinedInfo': {
                color: darkStatus.info.text,
                borderColor: darkStatus.info.border,
                '&:hover': { backgroundColor: darkStatus.info.surface, borderColor: darkStatus.info.text },
              },
              '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.3)',
                borderColor: 'rgba(255,255,255,0.12)',
              },
            }),
          },
          contained: {
            boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
            ...(isDark && {
              border: '1px solid rgba(255,255,255,0.08)',
            }),
            '&:hover': {
              boxShadow: isDark ? 'none' : '0 2px 6px rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
            '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'transparent',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255,255,255,0.23)' : undefined,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255,255,255,0.4)' : undefined,
            },
          },
        },
      },
      MuiFormControl: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
            '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: UI_TOKENS.text.bodySm,
            minHeight: UI_TOKENS.menu.itemMinHeight,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: UI_TOKENS.text.buttonSm,
            fontWeight: 600,
            minHeight: 48,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: 'none',
            backgroundImage: 'none',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'transparent'}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : undefined,
          },
          head: {
            backgroundColor: isDark ? '#151c28' : undefined,
            color: isDark ? '#9aa0a6' : undefined,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : undefined,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#2d3748' : undefined,
            fontSize: UI_TOKENS.text.helper,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            fontSize: UI_TOKENS.text.helper,
            fontWeight: 600,
            ...(isDark && {
              backgroundColor: 'rgba(255,255,255,0.09)',
              color: '#d7dce2',
              borderColor: 'rgba(255,255,255,0.2)',
              '& .MuiChip-icon': { color: 'inherit' },
              '& .MuiChip-deleteIcon': {
                color: 'rgba(255,255,255,0.55)',
                '&:hover': { color: '#fff' },
              },
              '&.MuiChip-colorPrimary': {
                backgroundColor: alpha(colors.primary, 0.2),
                color: colors.light,
                borderColor: alpha(colors.light, 0.45),
              },
              '&.MuiChip-colorSecondary': {
                backgroundColor: alpha(colors.secondary, 0.2),
                color: colors.light,
                borderColor: alpha(colors.light, 0.4),
              },
              '&.MuiChip-colorSuccess': {
                backgroundColor: darkStatus.success.surface,
                color: darkStatus.success.text,
                borderColor: darkStatus.success.border,
              },
              '&.MuiChip-colorWarning': {
                backgroundColor: darkStatus.warning.surface,
                color: darkStatus.warning.text,
                borderColor: darkStatus.warning.border,
              },
              '&.MuiChip-colorError': {
                backgroundColor: darkStatus.error.surface,
                color: darkStatus.error.text,
                borderColor: darkStatus.error.border,
              },
              '&.MuiChip-colorInfo': {
                backgroundColor: darkStatus.info.surface,
                color: darkStatus.info.text,
                borderColor: darkStatus.info.border,
              },
              '&.MuiChip-outlined': {
                backgroundColor: 'transparent',
              },
              '&.MuiChip-clickable:hover': {
                filter: 'brightness(1.18)',
              },
            }),
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: '1rem', fontWeight: 700 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const tenantDefaults = applyTenantThemeDefaults();
  const [currentTheme, setCurrentTheme] = useState<ColorTheme>(tenantDefaults.theme);
  const [customColors, setCustomColors] = useState<typeof colorPalettes.turquoise | null>(tenantDefaults.custom);
  const [isAuthenticated, setIsAuthenticated] = useState(() => isAuthSessionActive());
  const [appearance, setAppearanceState] = useState<AppearanceMode>(() => readStoredAppearance());
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => getSystemPrefersDark());

  const resolvedMode: 'light' | 'dark' =
    appearance === 'system' ? (systemPrefersDark ? 'dark' : 'light') : appearance;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    setSystemPrefersDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-color-scheme', resolvedMode);
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  const setAppearance = useCallback((next: AppearanceMode) => {
    setAppearanceState(next);
    try {
      localStorage.setItem(APPEARANCE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent('appearanceChanged', { detail: { appearance: next } }));
  }, []);

  const applyPublicBrandingTheme = useCallback(() => {
    const profile = getActiveTenantProfile();
    const colors = profile.themeColors;
    setCustomColors(colors);
    setCurrentTheme('custom' as ColorTheme);
    localStorage.setItem('turboiam-theme', 'custom');
    localStorage.setItem('turboiam-custom-theme', JSON.stringify(colors));
    ensureTenantBrandingDefaults();
    window.dispatchEvent(
      new CustomEvent('themeChanged', { detail: { theme: 'custom', colors } })
    );
  }, []);

  const applyThemeFromDatabase = useCallback(async () => {
    try {
      const response = await themeAPI.getThemeSettings();
      if (!response.data.success || !response.data.data) return false;

      const themeSettings = response.data.data;
      const parsedCustom = parseStoredCustomColors(
        typeof themeSettings.custom_colors === 'string'
          ? themeSettings.custom_colors
          : themeSettings.custom_colors
            ? JSON.stringify(themeSettings.custom_colors)
            : null
      ) ?? (themeSettings.custom_colors && typeof themeSettings.custom_colors === 'object'
        ? themeSettings.custom_colors
        : null);

      if (parsedCustom?.primary) {
        setCustomColors(parsedCustom);
        setCurrentTheme('custom' as ColorTheme);
        localStorage.setItem('turboiam-theme', 'custom');
        localStorage.setItem('turboiam-custom-theme', JSON.stringify(parsedCustom));
        window.dispatchEvent(
          new CustomEvent('themeChanged', { detail: { theme: 'custom', colors: parsedCustom } })
        );
        return true;
      }

      if (
        themeSettings.theme_name &&
        themeSettings.theme_name !== 'custom' &&
        colorPalettes[themeSettings.theme_name as keyof typeof colorPalettes]
      ) {
        setCurrentTheme(themeSettings.theme_name as keyof typeof colorPalettes);
        setCustomColors(null);
        localStorage.setItem('turboiam-theme', themeSettings.theme_name);
        localStorage.removeItem('turboiam-custom-theme');
        window.dispatchEvent(
          new CustomEvent('themeChanged', { detail: { theme: themeSettings.theme_name } })
        );
        return true;
      }
    } catch (error) {
      console.error('Error loading theme from database:', error);
    }
    return false;
  }, []);

  const applyThemeFromLocalStorage = useCallback(() => {
    const savedTheme = localStorage.getItem('turboiam-theme') as ColorTheme;
    const savedCustomTheme = parseStoredCustomColors(localStorage.getItem('turboiam-custom-theme'));

    if (savedCustomTheme) {
      setCustomColors(savedCustomTheme);
      setCurrentTheme('custom' as ColorTheme);
      window.dispatchEvent(
        new CustomEvent('themeChanged', { detail: { theme: 'custom', colors: savedCustomTheme } })
      );
      return;
    }

    if (savedTheme && savedTheme !== 'custom' && colorPalettes[savedTheme as keyof typeof colorPalettes]) {
      setCurrentTheme(savedTheme as keyof typeof colorPalettes);
      setCustomColors(null);
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: savedTheme } }));
    }
  }, []);

  // Pre-auth only: public tenant branding (login page). Never stomp saved org theme after sign-in.
  useEffect(() => {
    if (isAuthenticated) return;

    const syncPublicBranding = () => {
      if (!isAuthSessionActive()) {
        applyPublicBrandingTheme();
      }
    };

    syncPublicBranding();
    window.addEventListener('tenantBrandingLoaded', syncPublicBranding);
    return () => window.removeEventListener('tenantBrandingLoaded', syncPublicBranding);
  }, [isAuthenticated, applyPublicBrandingTheme]);

  useEffect(() => {
    const checkAuthStatus = () => {
      setIsAuthenticated(isAuthSessionActive());
    };

    checkAuthStatus();
    window.addEventListener('authChanged', checkAuthStatus);
    return () => window.removeEventListener('authChanged', checkAuthStatus);
  }, []);

  useEffect(() => {
    const loadTheme = async () => {
      if (isAuthenticated) {
        const loaded = await applyThemeFromDatabase();
        if (!loaded) {
          applyThemeFromLocalStorage();
        }
        return;
      }
      applyThemeFromLocalStorage();
    };

    void loadTheme();
  }, [isAuthenticated, applyThemeFromDatabase, applyThemeFromLocalStorage]);

  const handleSetCustomColors = useCallback(
    async (customTheme: typeof colorPalettes.turquoise) => {
      try {
        setCustomColors(customTheme);
        setCurrentTheme('custom' as ColorTheme);

        localStorage.setItem('turboiam-theme', 'custom');
        localStorage.setItem('turboiam-custom-theme', JSON.stringify(customTheme));

        if (isAuthenticated) {
          await themeAPI.saveThemeSettings({
            theme_name: 'custom',
            custom_colors: customTheme
          });
        }

        window.dispatchEvent(
          new CustomEvent('themeChanged', { detail: { theme: 'custom', colors: customTheme } })
        );
      } catch (error) {
        console.error('Error saving custom colors:', error);
      }
    },
    [isAuthenticated]
  );

  // Save theme to localStorage and database when changed
  const handleSetTheme = useCallback(async (theme: ColorTheme) => {
    try {
      console.log('💾 Saving theme:', theme);
      setCurrentTheme(theme);
      
      // Save to localStorage immediately for instant UI updates
      localStorage.setItem('turboiam-theme', theme);
      
      // Clear custom colors if switching to preset theme
      if (theme !== 'custom') {
        setCustomColors(null);
        localStorage.removeItem('turboiam-custom-theme');
        console.log('🗑️ Cleared custom theme data');
        
        // Save preset theme to database if authenticated
        if (isAuthenticated) {
          await themeAPI.saveThemeSettings({
            theme_name: theme,
            custom_colors: undefined
          });
        }
      } else {
        // Save custom theme to database if authenticated
        if (isAuthenticated && customColors) {
          localStorage.setItem('turboiam-custom-theme', JSON.stringify(customColors));
          await themeAPI.saveThemeSettings({
            theme_name: 'custom',
            custom_colors: customColors
          });
        }
      }
      
      // Force a re-render by updating the theme
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme } }));
      console.log('📡 Dispatched themeChanged event');
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  }, [isAuthenticated, customColors]);

  const colors = currentTheme === 'custom' && customColors ? customColors : colorPalettes[currentTheme as keyof typeof colorPalettes];

  const muiTheme = useMemo(
    () => buildMuiTheme(colors, resolvedMode),
    [colors, resolvedMode]
  );

  const refreshTheme = useCallback(async () => {
    if (isAuthenticated) {
      const loaded = await applyThemeFromDatabase();
      if (!loaded) {
        applyThemeFromLocalStorage();
      }
      return;
    }
    applyPublicBrandingTheme();
  }, [isAuthenticated, applyThemeFromDatabase, applyThemeFromLocalStorage, applyPublicBrandingTheme]);

  const value: ThemeContextType = {
    currentTheme,
    setTheme: handleSetTheme,
    availableThemes: Object.keys(colorPalettes) as (keyof typeof colorPalettes)[],
    colors,
    setCustomColors: handleSetCustomColors,
    refreshTheme,
    appearance,
    setAppearance,
    resolvedMode,
  };

  // Expose refreshTheme globally for debugging
  (window as any).refreshTurboIAMTheme = refreshTheme;
  (window as any).refreshProvysnTheme = refreshTheme;

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
